import { useContext, useEffect, useState } from "react";
import { useTranslation } from "../i18n";
import { motion, AnimatePresence } from "framer-motion";
import { RiCloseLine, RiImage2Fill, RiVideoLine, RiDeleteBinLine } from "react-icons/ri";
import type { Job } from "../types/job";
import { jobErrorText } from "../lib/jobError";
import { formatModel, hasScene, jobDisplayName, statusText } from "../lib/jobDisplay";
import { HostBridgeContext } from "../hostBridge";
import { withMwRoot } from "../lib/mwRoot";
import AudioPlayer from "./AudioPlayer";

interface AvatarGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: Job;
  onApprove: (jobId: Job) => Promise<void>;
  onDecline: (jobId: string) => Promise<void>;
  onDismiss?: (jobId: string) => Promise<void>;
  showHistory?: boolean;
}

export default function AvatarGenerationModal({
  isOpen,
  onClose,
  job,
  onApprove,
  onDecline,
  onDismiss,
  showHistory = false,
}: AvatarGenerationModalProps) {
  const { t, i18n } = useTranslation();
  const { onAddToScene } = useContext(HostBridgeContext);
  const [isApproving, setIsApproving] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [isAddingToScene, setIsAddingToScene] = useState(false);
  const [showSSML, setShowSSML] = useState(false);

  // Extract video data from job
  const videoData = job.result || (job as any).data || {};
  let videoUrl = videoData.videoUrl || job.resultUrl || "";

  // If outputPath exists (for image_to_avatar_generation), construct full URL
  if (!videoUrl && videoData.fileVirtualPath) {
    videoUrl = videoData.fileVirtualPath;
  }

  // Use sourceUrl for Before section (instead of resultUrl); a user-uploaded
  // avatar's preview can be a host-less /api/… path.
  const avatarImageUrl = withMwRoot(job.sourceUrl);

  // ai-avatar: prompttan üretilen avatar — kaynak görseli yok, sadece sonuç videosu + prompt
  const isAiAvatar = job.type === "ai-avatar" || job.type === "ai_avatar";
  // lipsync: önceki videoyu gösterme, sadece sonuç videosu daha iyi
  const isLipsync = job.type === "lipsync";
  // Doğal konuşan video: sonuç videosu + dönüştürülmüş anlatım sesi (ayrı dosya).
  const isTalking = job.type === "talking-avatar" || job.type === "talking_avatar";
  // Kaynak/önceki medyayı gizle, tek sütun sonuç göster
  const resultOnly = isAiAvatar || isLipsync || isTalking;
  const talkingAudioUrl: string = isTalking ? job.result?.audioUrl || "" : "";
  // The voice the speech was converted to, with its provider:
  // "Ava · Azure" / "Sarah · ElevenLabs" (no provider = ElevenLabs).
  const talkingVoiceLabel: string = (() => {
    const vd = isTalking ? job.result?.voiceData : null;
    const name = vd ? String(vd.voiceName || vd.voiceId || "").trim() : "";
    if (!name) return "";
    const provider = String(vd.provider || "").toLowerCase();
    const providerLabel =
      provider === "azure" || provider === "microsoft"
        ? t("modal.talkingVoiceProviderAzure", { defaultValue: "Azure" })
        : t("modal.talkingVoiceProviderElevenLabs", { defaultValue: "ElevenLabs" });
    return `${name} · ${providerLabel}`;
  })();
  const talkingWarnings: string[] =
    isTalking && Array.isArray(job.result?.warnings) ? job.result.warnings : [];
  // Failure text by code (job.error is the server's raw message).
  const talkingErrorText = (() => {
    if (!isTalking || !job.error) return null;
    const code = String(job.errorCode || "").replace(/^TALKING_AVATAR_/, "");
    const model = String(job.model || job.result?.model || job.metadata?.model || "");
    const key =
      code === "AVATAR_REJECTED" && /seedance/i.test(model)
        ? "errors.talkingAvatar.AVATAR_REJECTED_SEEDANCE"
        : `errors.talkingAvatar.${code}`;
    const text = code && i18n.exists(key) ? t(key) : t("errors.talkingAvatar.Generic");
    return `${text} ${t("errors.talkingAvatar.CreditNote")}`;
  })();

  // Some rows store a machine summary as the prompt, not the user's text:
  // "Lipsync: <url> + <url>", "Lipsync fal-ai", "Image-to-Avatar: a.png with x",
  // "AI Avatar (mode) — name".
  const userPrompt =
    job.prompt && !/^(Lipsync[: ]|Image-to-Avatar:|AI Avatar \()/.test(job.prompt)
      ? job.prompt
      : "";
  const script =
    (job as any).data?.script ||
    (job as any)?.script ||
    userPrompt ||
    (isTalking ? job.result?.narrationText : "") ||
    "";
  const replicaId = (job as any).data?.replicaId || "";
  const hasVideo = !!videoUrl && (job.status === "completed" || job.status === "inspect");

  // Function to remove SSML tags
  const removeSSMLTags = (text: string) => {
    return text.replace(/<[^>]*>/g, "");
  };

  const displayScript = showSSML ? script : removeSSMLTags(script);

  // Esc closes the modal (same callback as the X). Only attached while mounted.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const handleApprove = async () => {
    try {
      if (job?.type === "tts-avatar") {
        await handleAddToScene();
      }
      setIsApproving(true);
      await onApprove({
        ...job,
        hasVideo,
      });
      onClose();
    } finally {
      setIsApproving(false);
    }
  };

  const handleDecline = async () => {
    try {
      setIsDeclining(true);
      await onDecline(job.id);
      onClose();
    } finally {
      setIsDeclining(false);
    }
  };

  const handleDismiss = async () => {
    if (onDismiss) {
      await onDismiss(job.id);
      onClose();
    }
  };

  const handleAddToScene = async () => {
    try {
      setIsAddingToScene(true);
      onAddToScene({
        jobId: job.id,
        videoUrl,
        hasVideo,
        navId: job.navId,
        jobType: job.type,
        result: job.result,
      });
      onClose();
    } finally {
      setIsAddingToScene(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-60"
            onClick={onClose}
          />

          <motion.div
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 420, damping: 32 }}
            className="fixed top-16 right-4 bottom-4 w-[420px] max-w-[90vw] rounded-lg border border-white/20 bg-white backdrop-blur-xl shadow-2xl z-70 overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-fuchsia-500/5 pointer-events-none" />

            <div className="relative h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-black/5">
                <div className="space-y-0.5">
                  <p className="text-xs tracking-[0.08em] text-gray-500">
                    {t("modal.avatarPreviewUpper")}
                  </p>
                  {!isAiAvatar && (
                    <h2 className="text-lg font-semibold text-gray-800">
                      {isTalking ? t("jobNames.talking_avatar") : jobDisplayName(job, t, i18n)}
                    </h2>
                  )}
                  {(isLipsync || isTalking) && hasScene(job) && (
                    <p className="text-xs text-gray-500">
                      {t("jobCard.scene")} {job.sceneIndex}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {onDismiss && !showHistory && (
                    <button
                      onClick={handleDismiss}
                      className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                      title={t("actions.dismiss")}
                    >
                      <RiDeleteBinLine className="w-4 h-4 text-gray-600 hover:text-red-600" />
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    className="w-11 h-11 flex items-center justify-center hover:bg-black/5 rounded-lg transition-colors"
                  >
                    <RiCloseLine className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Visual Preview */}
                <div>
                  <h3 className="text-[11px] font-semibold text-gray-600 uppercase tracking-[0.08em] mb-2">
                    {t("modal.visualPreview")}
                  </h3>
                  {resultOnly ? (
                    // ai-avatar / lipsync: önceki/kaynak medya gösterilmez — tek sütun sonuç videosu
                    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                      <div className="px-3 py-2 text-xs font-semibold text-gray-600">
                        {t("modal.result")}
                      </div>
                      <div
                        className="bg-gray-50 flex items-center justify-center"
                        style={{ minHeight: "200px" }}
                      >
                        {hasVideo ? (
                          <video
                            src={videoUrl}
                            controls
                            className="w-full max-h-80 object-contain"
                          />
                        ) : (
                          <RiVideoLine className="w-7 h-7 text-gray-300" />
                        )}
                      </div>
                    </div>
                  ) : (
                    // Stacked, not side-by-side: Önce (source) small on top, Sonra
                    // (result) larger below — both fit the modal without scrolling.
                    <div className="space-y-3">
                      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-600">
                          {t("modal.before")}
                        </div>
                        <div className="bg-gray-50 flex items-center justify-center">
                          {avatarImageUrl ? (
                            avatarImageUrl.match(/\.(mp4|webm|ogg|mov)(\?|#|$)/i) ? (
                              <video
                                src={avatarImageUrl}
                                controls
                                className="max-h-[30vh] w-full object-contain"
                              />
                            ) : (
                              <img
                                src={avatarImageUrl}
                                alt={t("modal.before")}
                                className="max-h-[30vh] w-full object-contain"
                              />
                            )
                          ) : (
                            <RiImage2Fill className="w-7 h-7 text-gray-300 my-8" />
                          )}
                        </div>
                      </div>
                      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-600">
                          {t("modal.after")}
                        </div>
                        <div className="bg-gray-50 flex items-center justify-center">
                          {hasVideo ? (
                            <video
                              src={videoUrl}
                              controls
                              className="max-h-[50vh] w-full object-contain"
                            />
                          ) : (
                            <RiVideoLine className="w-7 h-7 text-gray-300 my-8" />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  <p className="text-[11px] text-gray-400 mt-2">{t("modal.aiContentWarning")}</p>
                </div>

                {isTalking && talkingAudioUrl && (
                  <div>
                    <h3 className="text-[11px] font-semibold text-gray-600 uppercase tracking-[0.08em] mb-2">
                      {t("modal.talkingVoiceAudio")}
                    </h3>
                    {talkingVoiceLabel && (
                      <p className="text-xs text-gray-700 font-semibold mb-2">
                        {talkingVoiceLabel}
                      </p>
                    )}
                    <AudioPlayer src={talkingAudioUrl} />
                  </div>
                )}

                {isTalking && hasVideo && !showHistory && (
                  <div className="border border-purple-200 rounded-lg p-3 bg-purple-50 text-xs text-purple-800 leading-snug">
                    {t("modal.talkingApplyNote")}
                  </div>
                )}

                {talkingWarnings.includes("BACKGROUND_REMOVAL_FAILED") && (
                  <div className="border border-amber-200 rounded-lg p-3 bg-amber-50 text-xs text-amber-800 leading-snug">
                    {t("modal.talkingBgRemovalFailed")}
                  </div>
                )}

                {/* 1:1 was asked for but the square crop failed: the 16:9 video is delivered. */}
                {talkingWarnings.includes("CROP_FAILED") && (
                  <div className="border border-amber-200 rounded-lg p-3 bg-amber-50 text-xs text-amber-800 leading-snug">
                    {t("modal.talkingCropFailed", {
                      defaultValue: "Kare kırpma başarısız oldu; video 16:9 olarak teslim edildi.",
                    })}
                  </div>
                )}

                {/* Parameters */}
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500 text-[11px] uppercase tracking-[0.08em]">
                      {t("jobCard.model")}
                    </p>
                    <p className="text-gray-800 font-semibold break-words">
                      {formatModel(job.model, t) || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-[11px] uppercase tracking-[0.08em]">
                      {t("jobCard.created")}
                    </p>
                    <p className="text-gray-800 font-semibold">
                      {new Date(job.createdAt).toLocaleDateString(
                        i18n.language === "tr" ? "tr-TR" : "en-US"
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-[11px] uppercase tracking-[0.08em]">
                      {t("jobCard.status")}
                    </p>
                    <p className="text-gray-800 font-semibold capitalize">
                      {statusText(job.status, t, i18n)}
                    </p>
                  </div>
                </div>

                {replicaId && (
                  <div className="border border-gray-200 rounded-lg p-3 bg-white text-sm">
                    <p className="text-gray-500 text-[11px] uppercase tracking-[0.08em]">
                      {t("jobCard.replicaId")}
                    </p>
                    <p className="font-mono text-purple-700 truncate">{replicaId}</p>
                  </div>
                )}

                {isAiAvatar && userPrompt && (
                  <div className="border border-gray-200 rounded-lg p-3 bg-white text-sm">
                    <p className="text-gray-500 text-[11px] tracking-[0.08em] mb-1 uppercase">
                      {t("modal.promptUpper")}
                    </p>
                    <div className="p-2 rounded-md bg-gray-50 border border-gray-100 max-h-28 overflow-y-auto text-xs text-gray-700 leading-snug whitespace-pre-wrap">
                      {userPrompt}
                    </div>
                  </div>
                )}

                {!isAiAvatar && script && (
                  <div className="border border-gray-200 rounded-lg p-3 bg-white text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-gray-500 text-[11px] tracking-[0.08em]">
                        {t("jobCard.avatarVoiceText")}
                      </p>
                      <button
                        onClick={() => setShowSSML(!showSSML)}
                        className="text-[10px] px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                      >
                        {showSSML ? t("actions.hideSSML") : t("actions.showSSML")}
                      </button>
                    </div>
                    <div className="p-2 rounded-md bg-gray-50 border border-gray-100 max-h-48 overflow-y-auto text-xs text-gray-700 leading-snug whitespace-pre-wrap">
                      {displayScript}
                    </div>
                  </div>
                )}

                {job.error && (
                  <div className="border border-red-200 rounded-lg p-3 bg-red-50 text-sm">
                    <p className="text-red-700 text-[11px] uppercase tracking-[0.08em] mb-1">
                      {t("jobCard.error")}
                    </p>
                    <p className="text-red-700 text-xs leading-snug">
                      {talkingErrorText || jobErrorText(job, t)}
                    </p>
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              {!showHistory && (
                <div className="border-t border-gray-200 px-4 py-3 flex gap-2 bg-white">
                  <button
                    onClick={handleDecline}
                    disabled={isDeclining || isApproving || isAddingToScene}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-white hover:bg-red-50 text-red-600 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-red-200"
                  >
                    {isDeclining ? t("actions.declining") : t("actions.decline")}
                  </button>

                  {(job?.status === "inspect" || job?.status === "completed") && hasVideo && (
                    <button
                      onClick={handleApprove}
                      disabled={isApproving || isDeclining || isAddingToScene}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    >
                      {isApproving ? t("actions.approving") : t("actions.approve")}
                    </button>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
